import "../accounts.css";
import { useEffect, useMemo, useState } from "react";
import { FiArrowDown, FiArrowUp, FiPlus } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { AppButton, FormField, SelectInput, TextInput } from "@/components/ui/primitives";
import { CollapsibleSection } from "@/components/ds";
import { DomainSidebar, DomainSidebarHeader, DomainTwoColumnContent, DomainTwoColumnLayout } from "@/components/ui/DomainSidebar";
import {
  useAccountTypes,
  useAccountColors,
  useAccountDeletionImpact,
  useAccounts,
  useAccountsBalance,
  useDeleteAccount,
} from "@/hooks/useAccounts";
import type { AccountBalance } from "@/hooks/useAccounts";
import { useStandardPeriodRange } from "@/hooks/useStandardPeriodRange";
import { useAuthStore } from "@/stores/useAuthStore";
import { formatCurrency, formatDate, formatNetAmount } from "@/utils/formatters";
import { parseDateLocal } from "@/utils/dateUtils";
import { NewAccountModal } from "../components/NewAccountModal.tsx";
import { AccountTransactionsModal } from "../components/AccountTransactionsModal.tsx";
import { confirmAction, showAppMessage } from "@/components/ui/confirmDialog";
import { DomainChatTrigger } from "@/domains/chat/components/DomainChatTrigger";
import { useNavigationState } from "@/hooks/useNavigationState";
import { SearchInput } from "@/components/ui/SearchInput";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { useSwipeActions } from "@/hooks/useSwipeActions";
import {
  compareAccounts,
  type AccountSort,
  type AccountSortDirection,
  type AccountSortField,
} from "../utils/accountSorting";
import { getAccountDeletionImpactDetails } from "../utils/deletionImpact";
import { getAccountCardBalance, hasAccountActivity, getAccountsPeriodTotals } from "../utils/cardBalance";
import { AccountCard, type AccountCardTone } from "../components/AccountCard";

interface SelectedAccount {
  account_id: number;
  name: string;
  description?: string | null;
  initial_balance?: number;
  account_type_id: number;
  billing_cycle_day?: number;
  payment_due_days?: number;
  credit_limit?: number;
  color_id?: number | null;
}

interface ExtendedAccountBalance extends AccountBalance {
  account_type_name: string;
  description?: string | null;
  credit_limit?: number;
  credit_used?: number;
  available_credit?: number;
  color_id?: number | null;
  billing_cycle_day?: number;
  payment_due_days?: number;
}

const formatMoney = (value: number) => formatCurrency(value);

const isCreditAccount = (account: ExtendedAccountBalance) => account.account_type_name.toUpperCase() === "CREDIT";

/** Credit figures for an account, or null when it is not a credit account. */
const getCreditCapacity = (account: ExtendedAccountBalance) => {
  if (!isCreditAccount(account) || account.credit_limit === undefined) return null;

  const limit = Number(account.credit_limit);
  const used = Number(account.credit_used ?? 0);
  const available = Number(account.available_credit ?? limit - used);

  return {
    limit,
    used,
    available,
    usage: limit > 0 ? Math.max((used / limit) * 100, 0) : 0,
  };
};

export const AccountsView = () => {
  const { t } = useTranslation(["common", "accounts", "dashboard"]);
  const { token } = useAuthStore();
  const { selectedPeriod, setSelectedPeriod, customStart, setCustomStart, customEnd, setCustomEnd, selectedRange } =
    useStandardPeriodRange("this-month", "accounts");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useNavigationState("accounts.searchTerm", "");
  const [showInactive, setShowInactive] = useNavigationState("accounts.showInactive", false);
  const [accountSort, setAccountSort] = useNavigationState<AccountSort>("accounts.sort", {
    field: "amount",
    direction: "desc",
  });
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount | null>(null);
  const [detailsAccount, setDetailsAccount] = useState<ExtendedAccountBalance | null>(null);
  const [deletingAccountId, setDeletingAccountId] = useState<number | null>(null);
  const {
    swipedId: swipedAccountId,
    setSwipedId: setSwipedAccountId,
    gestureRef,
    handlePressStart,
    handlePressMove,
    handlePressEnd,
  } = useSwipeActions<number>();

  const {
    data: accountsBalanceResponse,
    refetch: refetchAccountsBalance,
    error: accountsBalanceError,
    isLoading: accountsBalanceLoading,
  } = useAccountsBalance({
    billable_date_gte: selectedRange.start,
    billable_date_lte: selectedRange.end,
    monthly_expense: "desc",
  });
  const { data: accountsResponse, refetch: refetchAccounts, error: accountsError, isLoading: accountsLoading } = useAccounts();
  const accounts = useMemo(() => accountsResponse ?? [], [accountsResponse]);
  const { data: accountTypes = [] } = useAccountTypes();
  const { data: accountColors = [] } = useAccountColors();
  const accountColorById = useMemo(
    () => new Map(accountColors.map((accountColor) => [accountColor.id, accountColor.hex])),
    [accountColors],
  );
  const deleteAccountMutation = useDeleteAccount();
  const getAccountDeletionImpact = useAccountDeletionImpact();

  const accountTypeMap = useMemo(
    () => new Map(accountTypes.map((accountType) => [accountType.id, accountType.name])),
    [accountTypes],
  );

  const accountsBalance = useMemo<ExtendedAccountBalance[]>(() => {
    const accountById = new Map(accounts.map((account) => [account.id, account]));

    return (accountsBalanceResponse?.accounts_balance || []).flatMap((account) => {
      const fullAccount = accountById.get(account.account_id);
      if (!fullAccount) return [];

      return [{
        ...account,
        account_type_name: accountTypeMap.get(account.account_type_id) ?? t("accounts.accountTypeFallback"),
        description: fullAccount?.description ?? account.description ?? null,
        credit_limit: fullAccount?.credit_limit ?? account.credit_limit,
        credit_used: account.credit_used,
        available_credit: account.available_credit,
        color_id: fullAccount.color_id ?? null,
        billing_cycle_day: fullAccount?.billing_cycle_day,
        payment_due_days: fullAccount?.payment_due_days,
      }];
    });
  }, [accountsBalanceResponse, accounts, accountTypeMap, t]);

  const sortedAccounts = useMemo(
    () =>
      [...accountsBalance].sort(
        (a, b) => getAccountCardBalance(b) - getAccountCardBalance(a),
      ),
    [accountsBalance],
  );
  const filteredAccounts = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase();
    if (!query) return sortedAccounts;

    return sortedAccounts.filter((account) =>
      [account.name, account.description, account.account_type_name].some((value) =>
        value?.toLocaleLowerCase().includes(query),
      ),
    );
  }, [searchTerm, sortedAccounts]);
  const { activeAccounts, inactiveAccounts } = useMemo(() => {
    const sorted = [...filteredAccounts].sort((a, b) => compareAccounts(a, b, accountSort.field, accountSort.direction));
    return {
      activeAccounts: sorted.filter(hasAccountActivity),
      inactiveAccounts: sorted.filter((account) => !hasAccountActivity(account)),
    };
  }, [accountSort.direction, accountSort.field, filteredAccounts]);

  const totals = useMemo(() => getAccountsPeriodTotals(accountsBalance), [accountsBalance]);

  const totalBalanceLabel = formatNetAmount(totals.totalBalance, { plus: false });
  const openAccountEditor = (account: ExtendedAccountBalance) => {
    const fullAccount = accounts.find((acc) => acc.id === account.account_id);

    setSelectedAccount({
      account_id: account.account_id,
      name: account.name,
      description: fullAccount?.description ?? account.description ?? null,
      initial_balance: fullAccount?.initial_balance ?? 0,
      account_type_id: fullAccount?.account_type_id ?? account.account_type_id,
      billing_cycle_day: fullAccount?.billing_cycle_day,
      payment_due_days: fullAccount?.payment_due_days,
      credit_limit: fullAccount?.credit_limit ?? account.credit_limit,
      color_id: fullAccount?.color_id ?? null,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAccount(null);
  };

  const handleDeleteAccount = async (accountId: number) => {
    if (deletingAccountId !== null) return;
    setDeletingAccountId(accountId);
    try {
      let details: string[];
      try {
        details = getAccountDeletionImpactDetails(await getAccountDeletionImpact(accountId), t);
      } catch {
        showAppMessage({ title: t("accounts.deletionImpact.previewError"), tone: "danger" });
        return;
      }

      const confirmed = await confirmAction({
        title: t("common.prompts.deleteAccount"),
        body: t("common.prompts.cannotUndo", { defaultValue: "This action cannot be undone." }),
        details,
        confirmLabel: t("common.actions.delete", { defaultValue: "Delete" }),
        cancelLabel: t("common.actions.cancel", { defaultValue: "Cancel" }),
        tone: "danger",
      });
      if (!confirmed) return;

      await deleteAccountMutation.mutateAsync(accountId);
    } catch {
      showAppMessage({ title: t("common.errors.deleteAccount"), tone: "danger" });
    } finally {
      setDeletingAccountId(null);
    }
  };

  const handleAccountClick = (account: ExtendedAccountBalance) => {
    if (gestureRef.current.action === "swipe") {
      gestureRef.current.action = "tap";
      return;
    }

    if (swipedAccountId === account.account_id) {
      setSwipedAccountId(null);
      return;
    }

    setDetailsAccount(account);
  };

  const handleEditAccountFromList = (account: ExtendedAccountBalance) => {
    openAccountEditor(account);
    setSwipedAccountId(null);
  };

  const renderAccountCard = (account: ExtendedAccountBalance) => {
    const balance = getAccountCardBalance(account);
    const inflow = Number(account.monthly_deposit ?? 0);
    const outflow = Number(account.monthly_expense ?? 0);
    const capacity = getCreditCapacity(account);
    const accountType = account.account_type_name.toUpperCase();
    const typeLabel = account.account_type_name.charAt(0).toLocaleUpperCase() + account.account_type_name.slice(1).toLocaleLowerCase();
    const tone: AccountCardTone = accountType.includes("CREDIT")
      ? "lilac"
      : accountType.includes("CASH")
        ? "lime"
        : accountType.includes("SAVINGS")
          ? "sun"
          : "mist";
    const balanceLabel = t("accounts.balanceCycle");
    const displayBalance = balance < 0 ? `↓ -${formatMoney(Math.abs(balance))}` : formatMoney(balance);
    const creditDates = (() => {
      if (!capacity) return undefined;
      const cycleDay = account.billing_cycle_day;
      const paymentIn = account.payment_due_days;
      const rangeEnd = parseDateLocal(selectedRange.end);
      const cutDate = cycleDay
        ? new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), Math.min(cycleDay, 31))
        : null;
      const payDate = cutDate && paymentIn !== undefined
        ? new Date(cutDate.getFullYear(), cutDate.getMonth(), cutDate.getDate() + paymentIn)
        : null;
      const fmt = (date: Date) => formatDate(date.toISOString(), undefined, { day: "numeric", month: "short" });
      return {
        cut: cutDate ? t("accounts.creditCutDate", { date: fmt(cutDate) }) : undefined,
        payment: payDate ? t("accounts.creditPaymentDate", { date: fmt(payDate) }) : undefined,
      };
    })();

    return (
      <AccountCard
        key={account.account_id}
        name={account.name}
        typeLabel={typeLabel}
        tone={tone}
        color={account.color_id == null ? null : accountColorById.get(account.color_id) ?? null}
        balanceLabel={balanceLabel}
        balance={displayBalance}
        accessibleLabel={t("accounts.rowAccessibleLabel", {
          name: account.name,
          type: typeLabel,
          balance: displayBalance,
          income: formatMoney(inflow),
          expense: formatMoney(outflow),
          credit: capacity
            ? t("accounts.creditAccessibleSummary", {
                available: formatMoney(capacity.available),
                usage: t("accounts.creditUsage", { used: formatMoney(capacity.used), limit: formatMoney(capacity.limit) }),
              })
            : "",
        })}
        incomeLabel={t("accounts.flowIncome")}
        income={`${inflow > 0 ? "↑ " : ""}${formatMoney(inflow)}`}
        expenseLabel={t("accounts.flowExpense")}
        expense={`${outflow > 0 ? "↓ " : ""}${formatMoney(outflow)}`}
        creditInfo={capacity ? (
          <span>
            {t("accounts.availableCreditOf", {
              available: formatMoney(capacity.available),
              limit: formatMoney(capacity.limit),
            })}
          </span>
        ) : undefined}
        progress={capacity ? {
          percent: capacity.usage,
          label: t("accounts.creditUsedPercent", { percent: Math.round(capacity.usage) }),
          accessibleLabel: t("accounts.creditUsageValueText", { percent: Math.round(capacity.usage) }),
          valueText: t("accounts.creditUsageValueText", { percent: Math.round(capacity.usage) }),
        } : undefined}
        creditDates={creditDates}
        actions={[
          {
            kind: "edit",
            label: t("common:actions.edit"),
            onClick: () => handleEditAccountFromList(account),
          },
          {
            kind: "delete",
            label: t("common:actions.delete"),
            loading: deletingAccountId === account.account_id,
            disabled: deletingAccountId !== null,
            onClick: async () => {
              await handleDeleteAccount(account.account_id);
              setSwipedAccountId(null);
            },
          },
        ]}
        isSwiped={swipedAccountId === account.account_id}
        menuLabel={t("accounts.accountMenuLabel", { name: account.name })}
        onOpen={() => handleAccountClick(account)}
        onPointerDown={(event) => handlePressStart(event, account.account_id)}
        onPointerMove={(event) => handlePressMove(event, account.account_id)}
        onPointerUp={handlePressEnd}
        onPointerLeave={handlePressEnd}
        onPointerCancel={handlePressEnd}
      />
    );
  };

  useEffect(() => {
    if (token) {
      void refetchAccounts();
      void refetchAccountsBalance();
    }
  }, [token, refetchAccounts, refetchAccountsBalance]);

  if (!token) {
    return (
      <div className="app-page">
        <div className="app-container">
          <div className="app-surface border-[color:color-mix(in_srgb,var(--app-danger)_22%,var(--app-border)_78%)] bg-[color:color-mix(in_srgb,var(--app-danger)_8%,var(--app-surface)_92%)] p-6">
            <h3 className="mb-2 text-lg font-semibold text-[var(--app-text)]">{t("common.errors.missingAuthTokenTitle")}</h3>
            <p className="mb-4 text-[var(--app-text-secondary)]">{t("common.errors.missingAuthTokenBody")}</p>
            <button
              onClick={() => {
                useAuthStore.getState().signOut();
                window.location.href = "/auth/signin";
              }}
              className="rounded-full bg-[var(--app-danger)] px-4 py-2 font-medium text-[var(--app-on-primary)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--app-danger)_88%,black)]"
            >
              {t("accounts.logoutAgain")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const loadError = accountsBalanceError ?? accountsError;
  if (accountsLoading || accountsBalanceLoading) return <PageSkeleton />;

  if (loadError) {
    return (
      <div className="app-page flex items-center justify-center">
        <div className="app-surface border-[color:color-mix(in_srgb,var(--app-danger)_22%,var(--app-border)_78%)] bg-[color:color-mix(in_srgb,var(--app-danger)_8%,var(--app-surface)_92%)] px-8 py-6 text-center">
          <p className="font-medium text-[var(--app-text)]">{t("accounts.loadError")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page accounts-page">
      <div className="app-container max-w-none overflow-x-hidden">
        <div className="relative mx-auto w-full max-w-2xl min-w-0 overflow-x-hidden md:max-w-none">
          <DomainTwoColumnLayout>
            <DomainSidebar>
            <DomainSidebarHeader
              greeting={t("common:labels.greeting")}
              title={t("common:nav.accounts")}
              action={<DomainChatTrigger domain="accounts" />}
            />

            <section className="app-domain-sidebar-section">
              <p className="app-domain-sidebar-label">{t("accounts.balanceCycleTotal")}</p>
              <p className="app-domain-sidebar-value">{totalBalanceLabel}</p>
              <div className="accounts-balance-split">
                <div className="accounts-balance-part">
                  <span className="accounts-balance-icon inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--ds-card-lime)] text-[var(--ds-on-card)]" aria-hidden="true">
                    <FiArrowUp className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="app-domain-sidebar-label mb-0">{t("accounts.haveNow")}</p>
                    <p className="accounts-balance-amount">{formatMoney(totals.haveNow)}</p>
                  </div>
                </div>
                <div className="accounts-balance-part">
                  <span className="accounts-balance-icon inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--ds-card-flame)] text-[var(--ds-on-card)]" aria-hidden="true">
                    <FiArrowDown className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="app-domain-sidebar-label mb-0">{t("accounts.oweNow")}</p>
                    <p className="accounts-balance-amount">{formatMoney(totals.oweNow)}</p>
                  </div>
                </div>
              </div>
            </section>

            </DomainSidebar>

            <DomainTwoColumnContent>
            {accountsBalance.length > 0 ? (
              <section className="accounts-filter-bar sticky top-0 z-30 mb-5 grid grid-cols-2 items-center gap-3 py-2 lg:grid-cols-[minmax(0,1fr)_9.5rem_10rem]">
                <SearchInput
                  className="col-span-2 min-w-0 lg:col-span-1"
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder={t("common.actions.search")}
                  label={t("common.actions.search")}
                />
                <div className="min-w-0">
                  <label className="sr-only" htmlFor="accounts-cycle">{t("common.labels.cycle")}</label>
                  <SelectInput
                    id="accounts-cycle"
                    aria-label={t("common.labels.cycle")}
                    value={selectedPeriod ?? "this-month"}
                    onChange={(event) => setSelectedPeriod(event.target.value as typeof selectedPeriod)}
                    className="h-12 min-h-12 rounded-full border-[1.5px] border-[var(--ds-ink)] bg-[var(--ds-ground)] pl-4 pr-8 text-sm font-medium"
                  >
                    <option value="this-month">{t("common.periods.thisMonth")}</option>
                    <option value="last-month">{t("common.periods.lastMonth")}</option>
                    <option value="custom">{t("common.periods.custom")}</option>
                  </SelectInput>
                </div>
                <div className="min-w-0">
                  <label className="sr-only" htmlFor="accounts-sort">{t("accounts.sortByLabel")}</label>
                  <SelectInput
                    id="accounts-sort"
                    aria-label={t("accounts.sortByLabel")}
                    value={`${accountSort.field}:${accountSort.direction}`}
                    onChange={(event) => {
                      const [field, direction] = event.target.value.split(":");
                      setAccountSort({ field: field as AccountSortField, direction: direction as AccountSortDirection });
                    }}
                    className="h-12 min-h-12 rounded-full border-[1.5px] border-[var(--ds-ink)] bg-[var(--ds-ground)] pl-4 pr-8 text-sm font-medium"
                  >
                    <option value="amount:desc">{t("accounts.sortByAmount")} ↓</option>
                    <option value="amount:asc">{t("accounts.sortByAmount")} ↑</option>
                    <option value="name:asc">{t("accounts.sortByName")} ↑</option>
                    <option value="name:desc">{t("accounts.sortByName")} ↓</option>
                  </SelectInput>
                </div>
                {selectedPeriod === "custom" ? (
                  <div className="app-custom-cycle-panel col-span-2 grid grid-cols-2 gap-3 lg:col-span-3">
                    <FormField label={t("common.labels.startDate")} htmlFor="accounts-cycle-start">
                      <TextInput id="accounts-cycle-start" type="date" value={customStart} max={customEnd} onChange={(event) => setCustomStart(event.target.value)} />
                    </FormField>
                    <FormField label={t("common.labels.endDate")} htmlFor="accounts-cycle-end">
                      <TextInput id="accounts-cycle-end" type="date" value={customEnd} min={customStart} onChange={(event) => setCustomEnd(event.target.value)} />
                    </FormField>
                  </div>
                ) : null}
              </section>
            ) : null}
            {accounts.length === 0 ? (
              <section className="pb-6">
                <div className="app-panel-raised px-5 py-6">
                  <p className="text-base text-[var(--app-text-secondary)]">{t("accounts.emptyStateBody")}</p>
                  <AppButton
                    type="button"
                    tone="primary"
                    shape="pill"
                    className="mt-5"
                    onClick={() => setIsModalOpen(true)}
                    leadingIcon={<FiPlus className="h-4 w-4" />}
                  >
                    {t("accounts.addFirst")}
                  </AppButton>
                </div>
              </section>
            ) : (
              <section className="pb-8">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-medium leading-6 tracking-[-0.01em] text-[var(--ds-ink)]">{t("accounts.listTitle")}</h2>
                    <p className="mt-1 text-sm leading-5 text-[var(--ds-ink-muted)]">
                      {t("accounts.countLabel", { count: activeAccounts.length })}
                    </p>
                  </div>
                </div>

                {activeAccounts.length > 0 ? (
                  <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
                    {activeAccounts.map(renderAccountCard)}
                  </div>
                ) : filteredAccounts.length === 0 ? (
                  <p className="rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-raised)] p-5 text-sm text-[var(--ds-ink-muted)]">{t("accounts.noAccountsFound")}</p>
                ) : null}

                {inactiveAccounts.length > 0 ? (
                  <div className="mt-6">
                    <CollapsibleSection
                      title={t("accounts.inactiveTitle")}
                      subtitle={t("accounts.countLabel", { count: inactiveAccounts.length })}
                      open={showInactive}
                      onOpenChange={setShowInactive}
                      toggleLabel={(open) =>
                        t(open ? "dashboard.collapseSection" : "dashboard.expandSection", { section: t("accounts.inactiveTitle") })
                      }
                    >
                      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
                        {inactiveAccounts.map(renderAccountCard)}
                      </div>
                    </CollapsibleSection>
                  </div>
                ) : null}
              </section>
            )}
            </DomainTwoColumnContent>
          </DomainTwoColumnLayout>
        </div>

        <NewAccountModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSuccess={() => {
            refetchAccountsBalance();
            refetchAccounts();
            handleCloseModal();
          }}
          account={selectedAccount}
        />
        {detailsAccount ? (
          <AccountTransactionsModal
            accountId={detailsAccount.account_id}
            accountName={detailsAccount.name}
            periodBalance={getAccountCardBalance(detailsAccount)}
            start={selectedRange.start}
            end={selectedRange.end}
            onClose={() => setDetailsAccount(null)}
          />
        ) : null}
      </div>
    </div>
  );
};
